/**
 * Apartman veri modeli normalizasyonu
 * Eski alan adlarini tek bir kanonik sekle cevirir.
 */

export interface ApartmentRecord {
    id?: string;
    number: number;
    residentName: string;
    phone: string;
    email: string;
    residentCount: number;
    status: 'owner' | 'tenant' | 'empty';
    ownerName: string;
    moveInDate: string;
    notes?: string;
    balance?: number;
    createdAt?: string;
    updatedAt?: string;
    _docId?: string;
}

function toApartmentNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number.parseInt(value, 10);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return 0;
}

function toResidentStatus(value: unknown): ApartmentRecord['status'] {
    if (value === 'owner' || value === 'tenant' || value === 'empty') {
        return value;
    }

    if (value === 'occupied') {
        return 'tenant';
    }

    return 'empty';
}

export function normalizeApartmentRecord(source: Record<string, any> = {}, fallbackId?: string): ApartmentRecord {
    const isOwner = typeof source.isOwner === 'boolean' ? source.isOwner : undefined;
    const normalizedStatus = toResidentStatus(
        source.status ?? (isOwner === true ? 'owner' : isOwner === false ? 'tenant' : 'empty')
    );

    return {
        id: source.id || fallbackId,
        _docId: fallbackId || source._docId,
        number: toApartmentNumber(source.number ?? source.apartmentNo ?? source.apartmentNumber),
        residentName: String(source.residentName || '').trim(),
        phone: String(source.phone ?? source.contactNumber ?? '').trim(),
        email: String(source.email || '').trim(),
        residentCount: Number.isFinite(source.residentCount) ? source.residentCount : 0,
        status: normalizedStatus,
        ownerName: String(source.ownerName || '').trim(),
        moveInDate: String(source.moveInDate || '').trim(),
        notes: typeof source.notes === 'string' ? source.notes : undefined,
        balance: Number.isFinite(source.balance) ? source.balance : 0,
        createdAt: typeof source.createdAt === 'string' ? source.createdAt : undefined,
        updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : undefined
    };
}

export function serializeApartmentRecord(source: Record<string, any> = {}): ApartmentRecord {
    const normalized = normalizeApartmentRecord(source, source.id);
    const { _docId, ...recordWithoutDocId } = normalized;

    return {
        ...recordWithoutDocId,
        id: normalized.id || `apt_${normalized.number}`
    };
}
